import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { TfiAngleLeft, TfiSearch, TfiAlert } from "react-icons/tfi";
import axios from "axios";
import { useNavContext } from "../context/nav_context";
import {
  api_v3_list_of_players_admin,
  api_v3_merge_players,
} from "../utils/constants";

const AdminMergePlayers = () => {
  const { user, accessToken: ctxToken } = useNavContext();
  const accessToken = ctxToken || localStorage.getItem("access_token");

  const [searchSource, setSearchSource] = useState("");
  const [searchTarget, setSearchTarget] = useState("");
  const [sourceResults, setSourceResults] = useState([]);
  const [targetResults, setTargetResults] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const doSearch = async (query, setResults) => {
    if (!query.trim()) { setResults([]); return; }
    try {
      const { data } = await axios.get(api_v3_list_of_players_admin(null));
      const filtered = data.filter(
        (p) => p.username.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filtered);
      setError(null);
    } catch (err) {
      console.error("Error searching players:", err);
      setError("Error al buscar jugadores");
    }
  };

  const selectSource = (player) => {
    setSelectedSource(player);
    setSourceResults([]);
    setSearchSource(player.username);
    setResult(null);
    setError(null);
  };

  const selectTarget = (player) => {
    setSelectedTarget(player);
    setTargetResults([]);
    setSearchTarget(player.username);
    setResult(null);
    setError(null);
  };

  const handleMerge = async () => {
    setMerging(true);
    setError(null);
    try {
      const { data } = await axios.post(
        api_v3_merge_players(),
        {
          sourcePlayerId: selectedSource.playerId,
          targetPlayerId: selectedTarget.playerId,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setResult(data);
      setShowConfirm(false);
    } catch (err) {
      console.error("Error merging players:", err);
      setError(
        err.response?.data?.detail || "Error al fusionar jugadores"
      );
    } finally {
      setMerging(false);
    }
  };

  if (!user || !user.isSuperAdmin) {
    return (
      <Canvas>
        <EmptyState>
          Lo siento, pero estás intentando acceder a donde no debes.
        </EmptyState>
      </Canvas>
    );
  }

  return (
    <Canvas>
      <Content>
        <HeaderRow>
          <PageTitle>MERGE JUGADORES</PageTitle>
          <BackLink to="/admin/"><TfiAngleLeft /> ADMIN HOME</BackLink>
        </HeaderRow>

        {error && <AlertBox>{error}</AlertBox>}

        {result ? (
          <ResultCard>
            <ResultTitle>FUSIÓN COMPLETADA</ResultTitle>
            <ResultLine>Source: <strong>{result.sourcePlayerId}</strong></ResultLine>
            <ResultLine>Target: <strong>{result.targetPlayerId}</strong></ResultLine>
            <ResultLine>Partidos actualizados: {result.matchesUpdated}</ResultLine>
            <ResultLine>Inscripciones actualizadas: {result.tournamentPlayersUpdated}</ResultLine>
            <ResultLine>Clasificaciones actualizadas: {result.classificationsUpdated}</ResultLine>
            <ResultLine>Playoffs actualizados: {result.playoffsUpdated}</ResultLine>
            <ResultLine>Streamings actualizados: {result.matchStreamingUpdated}</ResultLine>
            {result.warnings.length > 0 && (
              <>
                <WarningTitle>ADVERTENCIAS</WarningTitle>
                {result.warnings.map((w, i) => (
                  <WarningLine key={i}>{w}</WarningLine>
                ))}
              </>
            )}
            <ResetBtn onClick={() => {
              setResult(null);
              setSelectedSource(null);
              setSelectedTarget(null);
              setSearchSource("");
              setSearchTarget("");
            }}>
              NUEVA FUSIÓN
            </ResetBtn>
          </ResultCard>
        ) : (
          <>
            <SectionCard>
              <SectionTitle>CUENTA ORIGEN (será absorbida)</SectionTitle>
              <SearchRow>
                <SearchInput
                  placeholder="Buscar por username..."
                  value={searchSource}
                  onChange={(e) => setSearchSource(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") doSearch(searchSource, setSourceResults);
                  }}
                />
                <SearchBtn onClick={() => doSearch(searchSource, setSourceResults)}>
                  <TfiSearch /> BUSCAR
                </SearchBtn>
              </SearchRow>
              {selectedSource && (
                <SelectedBadge>
                  {selectedSource.username}
                  <ChangeBtn onClick={() => {
                    setSelectedSource(null);
                    setSearchSource("");
                  }}>CAMBIAR</ChangeBtn>
                </SelectedBadge>
              )}
              {sourceResults.length > 0 && !selectedSource && (
                <ResultList>
                  {sourceResults.map((player) => (
                    <PlayerRow key={player.playerId}>
                      <PlayerName>{player.username}</PlayerName>
                      <SelectBtn onClick={() => selectSource(player)}>SELECCIONAR</SelectBtn>
                    </PlayerRow>
                  ))}
                </ResultList>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>CUENTA DESTINO (se queda con los datos)</SectionTitle>
              <SearchRow>
                <SearchInput
                  placeholder="Buscar por username..."
                  value={searchTarget}
                  onChange={(e) => setSearchTarget(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") doSearch(searchTarget, setTargetResults);
                  }}
                />
                <SearchBtn onClick={() => doSearch(searchTarget, setTargetResults)}>
                  <TfiSearch /> BUSCAR
                </SearchBtn>
              </SearchRow>
              {selectedTarget && (
                <SelectedBadge>
                  {selectedTarget.username}
                  <ChangeBtn onClick={() => {
                    setSelectedTarget(null);
                    setSearchTarget("");
                  }}>CAMBIAR</ChangeBtn>
                </SelectedBadge>
              )}
              {targetResults.length > 0 && !selectedTarget && (
                <ResultList>
                  {targetResults.map((player) => (
                    <PlayerRow key={player.playerId}>
                      <PlayerName>{player.username}</PlayerName>
                      <SelectBtn onClick={() => selectTarget(player)}>SELECCIONAR</SelectBtn>
                    </PlayerRow>
                  ))}
                </ResultList>
              )}
            </SectionCard>

            {selectedSource && selectedTarget && (
              <SectionCard>
                <SummaryTitle>RESUMEN DE LA FUSIÓN</SummaryTitle>
                <SummaryRow>
                  <SummaryLabel>ORIGEN:</SummaryLabel>
                  <SummaryValue>{selectedSource.username} ({selectedSource.playerId})</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>DESTINO:</SummaryLabel>
                  <SummaryValue>{selectedTarget.username} ({selectedTarget.playerId})</SummaryValue>
                </SummaryRow>
                {selectedSource.playerId === selectedTarget.playerId && (
                  <WarningLine>No puedes fusionar una cuenta consigo misma.</WarningLine>
                )}
                {selectedSource.playerId !== selectedTarget.playerId && (
                  <MergeBtn onClick={() => setShowConfirm(true)} disabled={merging}>
                    {merging ? "FUSIONANDO..." : "FUSIONAR CUENTAS"}
                  </MergeBtn>
                )}
              </SectionCard>
            )}
          </>
        )}

        {showConfirm && (
          <Overlay>
            <ConfirmBox>
              <ConfirmIcon><TfiAlert /></ConfirmIcon>
              <ConfirmTitle>¿FUSIONAR CUENTAS?</ConfirmTitle>
              <ConfirmText>
                Esta acción es <strong>IRREVERSIBLE</strong>. La cuenta origen
                (<strong>{selectedSource.username}</strong>) será desactivada y todos
                sus datos (partidos, inscripciones, clasificaciones...) se transferirán
                a la cuenta destino (<strong>{selectedTarget.username}</strong>).
                <br /><br />
                El usuario podrá iniciar sesión con cualquiera de los dos Discord IDs.
              </ConfirmText>
              <ConfirmActions>
                <CancelBtn onClick={() => setShowConfirm(false)}>CANCELAR</CancelBtn>
                <DangerBtn onClick={handleMerge} disabled={merging}>
                  {merging ? "FUSIONANDO..." : "CONFIRMAR FUSIÓN"}
                </DangerBtn>
              </ConfirmActions>
            </ConfirmBox>
          </Overlay>
        )}
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`background: var(--v-canvas); min-height: 100vh; display: flex; justify-content: center; padding: 4rem 1.5rem;`;
const Content = styled.div`width: 100%; max-width: 640px;`;
const PageTitle = styled.h1`font-family: Anton, sans-serif; font-size: clamp(36px, 6vw, 56px); font-weight: 400; text-transform: uppercase; line-height: 1; color: var(--v-fg); margin: 0;`;
const EmptyState = styled.p`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg-muted); text-align: center; padding: 4rem 1rem;`;
const HeaderRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 2.5rem; flex-wrap: wrap; gap: 1rem;`;
const BackLink = styled(Link)`display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-mint); text-decoration: none; padding: 0.5rem 0.9rem; border: 1px solid rgba(60,255,208,0.2); border-radius: 24px; transition: background 0.15s ease; &:hover { background: rgba(60,255,208,0.04); }`;
const AlertBox = styled.div`background: rgba(255,59,59,0.1); border: 1px solid rgba(255,59,59,0.2); color: #ff3b3b; font-family: var(--v-font-mono); font-size: 11px; padding: 0.6rem 0.85rem; border-radius: 10px; margin-bottom: 1rem;`;
const SectionCard = styled.div`background: var(--v-surface); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; padding: 1.5rem; margin-bottom: 1.25rem;`;
const SectionTitle = styled.h3`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--v-fg-muted); margin: 0 0 1rem;`;
const SearchRow = styled.div`display: flex; gap: 0.5rem; margin-bottom: 1rem;`;
const SearchInput = styled.input`flex: 1; padding: 0.6rem 0.85rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; color: var(--v-fg); font-family: var(--v-font-body); font-size: 13px; outline: none; transition: border-color 0.15s ease; &:focus { border-color: var(--v-mint); } &::placeholder { color: var(--v-fg-muted); opacity: 0.4; }`;
const SearchBtn = styled.button`display: inline-flex; align-items: center; gap: 0.4rem; background: var(--v-mint); border: none; color: #000; font-family: var(--v-font-mono); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem 1rem; border-radius: 20px; cursor: pointer; transition: opacity 0.15s ease; &:hover { opacity: 0.85; }`;
const ResultList = styled.div`background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 14px; overflow: hidden;`;
const PlayerRow = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 0.6rem 0.85rem; border-bottom: 1px solid rgba(255,255,255,0.04); &:last-child { border-bottom: none; }`;
const PlayerName = styled.span`font-family: var(--v-font-body); font-size: 14px; font-weight: 600; color: var(--v-fg);`;
const SelectBtn = styled.button`background: var(--v-mint); border: none; color: #000; font-family: var(--v-font-mono); font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.35rem 0.85rem; border-radius: 16px; cursor: pointer; transition: opacity 0.15s ease; &:hover { opacity: 0.85; }`;
const SelectedBadge = styled.div`display: flex; align-items: center; justify-content: space-between; background: rgba(60,255,208,0.06); border: 1px solid rgba(60,255,208,0.15); border-radius: 12px; padding: 0.6rem 0.85rem; font-family: var(--v-font-body); font-size: 14px; font-weight: 600; color: var(--v-mint);`;
const ChangeBtn = styled.button`background: transparent; border: 1px solid rgba(255,255,255,0.15); color: var(--v-fg-muted); font-family: var(--v-font-mono); font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.3rem 0.7rem; border-radius: 12px; cursor: pointer; transition: all 0.15s ease; &:hover { border-color: var(--v-white); color: var(--v-white); }`;
const SummaryTitle = styled.h4`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--v-fg-muted); margin: 0 0 1rem;`;
const SummaryRow = styled.div`display: flex; gap: 0.5rem; margin-bottom: 0.5rem; font-family: var(--v-font-body); font-size: 13px;`;
const SummaryLabel = styled.span`color: var(--v-fg-muted); min-width: 80px;`;
const SummaryValue = styled.span`color: var(--v-fg); font-weight: 600; word-break: break-all;`;
const WarningLine = styled.p`font-family: var(--v-font-body); font-size: 12px; color: #ff3b3b; margin: 0.5rem 0 0;`;
const MergeBtn = styled.button`width: 100%; margin-top: 1rem; background: #ff4d4d; border: none; color: #fff; font-family: var(--v-font-mono); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.75rem 1.25rem; border-radius: 14px; cursor: pointer; transition: opacity 0.15s ease; &:hover { opacity: 0.85; } &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; justify-content: center; align-items: center; z-index: 9999;`;
const ConfirmBox = styled.div`background: var(--v-surface); border: 1px solid rgba(255,77,77,0.2); border-radius: 20px; padding: 2.5rem; text-align: center; max-width: 480px; width: 90%;`;
const ConfirmIcon = styled.div`font-size: 2.5rem; color: #ff4d4d; margin-bottom: 0.5rem; line-height: 1; display: flex; justify-content: center;`;
const ConfirmTitle = styled.h3`font-family: var(--v-font-display); font-size: 28px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #ff4d4d; margin: 0 0 1rem;`;
const ConfirmText = styled.p`font-family: var(--v-font-body); font-size: 14px; line-height: 1.6; color: var(--v-secondary); margin: 0 0 2rem; strong { color: var(--v-white); }`;
const ConfirmActions = styled.div`display: flex; gap: 0.75rem; justify-content: center;`;
const CancelBtn = styled.button`background: transparent; border: 1px solid rgba(255,255,255,0.15); color: var(--v-fg-muted); font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem 1.25rem; border-radius: 24px; cursor: pointer; transition: all 0.15s ease; &:hover { border-color: var(--v-white); color: var(--v-white); }`;
const DangerBtn = styled.button`background: #ff4d4d; border: none; color: #fff; font-family: var(--v-font-mono); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem 1.25rem; border-radius: 24px; cursor: pointer; transition: opacity 0.15s ease; &:hover { opacity: 0.85; } &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const ResultCard = styled.div`background: var(--v-surface); border: 1px solid rgba(60,255,208,0.15); border-radius: 20px; padding: 1.5rem;`;
const ResultTitle = styled.h3`font-family: var(--v-font-mono); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--v-mint); margin: 0 0 1rem;`;
const ResultLine = styled.p`font-family: var(--v-font-body); font-size: 13px; color: var(--v-fg); margin: 0 0 0.4rem; strong { color: var(--v-fg-muted); font-weight: 400; font-family: var(--v-font-mono); font-size: 11px; }`;
const WarningTitle = styled.h4`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #ff3b3b; margin: 1rem 0 0.5rem;`;
const ResetBtn = styled.button`margin-top: 1.25rem; background: var(--v-mint); border: none; color: #000; font-family: var(--v-font-mono); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem 1.25rem; border-radius: 24px; cursor: pointer; transition: opacity 0.15s ease; &:hover { opacity: 0.85; }`;

export default AdminMergePlayers;
